import React, { useEffect, useMemo, useState } from "react";

import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { useDealActions, useMeasuresSelector } from "store";
import type { CardConfig, SummaryCardConfig } from "store/types";

import { SectionLabel } from "./SectionLabel";

interface Props {
  config: SummaryCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
}

export const SummarySettings: React.FC<Props> = ({ config, onUpdateConfig }) => {
  const { getMeasuresAction } = useDealActions();
  const measures = useMeasuresSelector();
  const [loadingMeasures, setLoadingMeasures] = useState(false);

  useEffect(() => {
    if (measures.length === 0) {
      setLoadingMeasures(true);
      getMeasuresAction().finally(() => setLoadingMeasures(false));
    }
  }, [measures.length, getMeasuresAction]);

  const measureOptions = useMemo(() => measures.map((m) => ({ value: m.name, label: m.name })), [measures]);

  return (
    <Box>
      <SectionLabel>Measure</SectionLabel>
      <Typography sx={{ fontSize: 11, color: "#9CA3AF", mb: 1 }}>
        Select a measure to display Total, Average, Min, Max
      </Typography>
      <Autocomplete
        size="small"
        options={measureOptions}
        value={measureOptions.find((o) => o.value === config.parameter) || null}
        onChange={(_, v) => onUpdateConfig({ ...config, parameter: v?.value || undefined })}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        loading={loadingMeasures}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search measures..."
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingMeasures ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
            sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
          />
        )}
      />
    </Box>
  );
};
